import { ALL_POKEMON } from './allPokemon';
import { POKEMON_DATA } from './pokemonData';

// All base species available in Pokémon Champions (Regulation M-A)
// Source: https://www.serebii.net/pokemonchampions/pokemon.shtml
const REG_MA_SPECIES_IDS = new Set([
  // Gen 1
  3, 6, 9, 15, 18, 24, 25, 26, 36, 38, 59, 65, 68, 71, 80, 94,
  115, 121, 127, 128, 130, 132, 134, 135, 136, 142, 143, 149,
  // Gen 2
  154, 157, 160, 168, 181, 184, 186, 196, 197, 199, 205, 208,
  212, 214, 227, 229, 248,
  // Gen 3
  279, 282, 302, 306, 308, 310, 319, 323, 324, 334, 350, 351,
  354, 358, 359, 362,
  // Gen 4
  389, 392, 395, 405, 407, 409, 411, 428, 442, 445, 448, 450,
  454, 460, 461, 464, 470, 471, 472, 473, 475, 478, 479,
  // Gen 5
  497, 500, 503, 505, 510, 512, 514, 516, 530, 531, 534, 547,
  553, 563, 569, 571, 579, 584, 587, 609, 614, 618, 623, 635, 637,
  // Gen 6
  652, 655, 658, 660, 663, 666, 670, 671, 675, 676, 678, 681,
  683, 685, 693, 695, 697, 699, 700, 701, 702, 706, 707, 709,
  711, 713, 715,
  // Gen 7
  724, 727, 730, 733, 740, 745, 748, 750, 752, 758, 763, 765,
  766, 778, 780, 784,
  // Gen 8
  823, 841, 842, 844, 855, 858, 866, 867, 869, 877, 887, 899,
  900, 902, 903,
  // Gen 9
  908, 911, 914, 925, 934, 936, 937, 939, 952, 956, 959, 964,
  968, 970, 981, 983, 1013, 1018, 1019,
]);

// Only these 54 species have Mega Evolutions available in Champions.
// Source: https://www.serebii.net/pokemonchampions/pokemon.shtml
const REG_MA_MEGA_SPECIES_IDS = new Set([
  // Gen 1
  3, 6, 9, 15, 18, 36, 71, 80, 94, 115, 121, 127, 130, 142, 149,
  // Gen 2
  154, 160, 181, 208, 212, 214, 227, 229, 248,
  // Gen 3
  282, 302, 306, 308, 310, 319, 323, 334, 354, 358, 359, 362,
  // Gen 4
  428, 475, 478,
  // Gen 5
  500, 530, 531, 609, 623,
  // Gen 6
  652, 655, 658, 670, 678, 701,
  // Gen 7
  740, 780,
  // Gen 9
  952, 970,
]);

// Form suffixes that are never competitive — always exclude these regardless
// of whether the base species is whitelisted.
const EXCLUDED_SUFFIXES = [
  '-gmax',           // Gigantamax — Dynamax not in Reg M-A
  '-totem',          // Totem forms — Alola trials, same stats as base
  '-battle-bond',    // Greninja Battle Bond — banned in Champions
  '-ash',            // Ash-Greninja — banned in Champions
  // Pikachu costume/cap variants — cosmetic only, same stats
  '-rock-star', '-belle', '-pop-star', '-phd', '-libre', '-cosplay',
  '-original-cap', '-hoenn-cap', '-sinnoh-cap', '-unova-cap',
  '-kalos-cap', '-alola-cap', '-partner-cap', '-starter', '-world-cap',
];

function isExcludedForm(key) {
  return EXCLUDED_SUFFIXES.some(suffix => key.includes(suffix));
}

// Resolve the base species ID for any key (handles forms and megas).
// Strips parts from the right until it finds a POKEMON_DATA entry with
// a base dex ID (< 10000), e.g.:
//   "charizard-mega-x" → "charizard" (6)
//   "tauros-paldea-combat-breed" → "tauros" (128)
//   "rotom-heat" → "rotom" (479)
//   "dragapult" → "dragapult" (887)
function getBaseSpeciesId(key) {
  const parts = key.split('-');
  for (let len = parts.length; len >= 1; len--) {
    const candidate = parts.slice(0, len).join('-');
    const data = POKEMON_DATA[candidate];
    if (data && data.id < 10000) return data.id;
  }
  return null;
}

// Reg M-A pool: whitelisted base species + confirmed Champions Megas + regional
// variants, minus G-Max, totem, Battle Bond, and cosmetic-only forms.
export const REG_MA_POKEMON = ALL_POKEMON.filter(p => {
  if (isExcludedForm(p.key)) return false;
  const isMega = p.key.includes('-mega');
  const baseId = getBaseSpeciesId(p.key);
  if (baseId === null) return false;
  if (isMega) return REG_MA_MEGA_SPECIES_IDS.has(baseId);
  return REG_MA_SPECIES_IDS.has(baseId);
});

export const REGULATIONS = [
  { value: 'all', label: 'All Pokémon' },
  { value: 'reg-ma', label: 'Reg M-A (Champions)' },
];

export function getPokemonPool(regulation) {
  return regulation === 'reg-ma' ? REG_MA_POKEMON : ALL_POKEMON;
}
